// modules/LotterySystem.js
// 抽奖系统模块
import DebugHelper from './DebugHelper.js';
import DropSystem from './DropSystem.js';
import { deepClone } from './utils/PlayerDataSync.js';

export default class LotterySystem {
    /**
     * 抽奖配置
     */
    static LOTTERY_CONFIG = {
        // 奖品池（按概率排序）
        prizes: [
            // 普通奖品（70%）
            { id: 'cannon_ammo', name: '主炮弹药', type: 'consumable', chance: 25, count: 10 },
            { id: 'se_ammo', name: 'S-E弹药', type: 'consumable', chance: 20, count: 5 },
            { id: 'iron', name: '铁', type: 'material', chance: 15, count: 5 },
            { id: 'copper', name: '铜', type: 'material', chance: 10, count: 3 },
            
            // 稀有奖品（25%）
            { id: 'random_fragment_tier1', name: '装备碎片', type: 'fragment', chance: 15, tier: 1 },
            { id: 'corrosion_layer_kit', name: '抗腐蚀图层补充包', type: 'consumable', chance: 5, amount: 50 },
            { id: 'repair_kit', name: '修理包', type: 'consumable', chance: 5, repairAmount: 50 },
            
            // 史诗奖品（4%）
            { id: 'random_equipment_tier1', name: '随机装备', type: 'vehicle_equipment', chance: 3, tier: 1 },
            { id: 'lottery_ticket_tier2', name: '高级抽奖券', type: 'consumable', chance: 1 },
            
            // 传说奖品（1%）
            { id: 'random_equipment_tier2', name: '高级随机装备', type: 'vehicle_equipment', chance: 0.5, tier: 2 },
            { id: 'golden_lottery_ticket', name: '黄金抽奖券', type: 'consumable', chance: 0.5 }
        ],
        
        // 大奖配置
        jackpot: {
            chance: 0.1, // 0.1%概率
            prizes: [
                { id: 'random_equipment_tier3', name: '史诗装备', type: 'vehicle_equipment', tier: 3 },
                { id: 'legendary_fragment', name: '传说碎片', type: 'fragment', tier: 4, count: 10 }
            ]
        }
    };
    
    /**
     * 执行抽奖
     * @param {Object} player - 玩家对象
     * @param {string} ticketType - 抽奖券类型
     * @returns {Object} 抽奖结果 {success: boolean, prize: Object, message: string}
     */
    static drawLottery(player, ticketType = 'lottery_ticket_tier1') {
        if (!player || !player.inventory) {
            return {
                success: false,
                prize: null,
                message: '玩家数据无效'
            };
        }
        
        // 检查是否有抽奖券
        const ticketIndex = player.inventory.findIndex(
            item => item.id === ticketType || item.name === '抽奖券'
        );
        
        if (ticketIndex === -1) {
            return {
                success: false,
                prize: null,
                message: '没有抽奖券！'
            };
        }
        
        // 消耗抽奖券
        player.inventory.splice(ticketIndex, 1);
        
        // 检查是否中大奖
        if (Math.random() * 100 < this.LOTTERY_CONFIG.jackpot.chance) {
            const jackpotPrize = this.selectJackpotPrize();
            this.addPrizeToInventory(player, jackpotPrize);
            
            DebugHelper.log(`LotterySystem: 中大奖！获得 ${jackpotPrize.name}`);
            
            return {
                success: true,
                prize: jackpotPrize,
                isJackpot: true,
                message: `🎉 大奖！获得 ${jackpotPrize.name}！`
            };
        }
        
        // 普通抽奖
        const prize = this.selectPrize();
        this.addPrizeToInventory(player, prize);
        
        DebugHelper.log(`LotterySystem: 抽奖获得 ${prize.name}`);
        
        return {
            success: true,
            prize: prize,
            isJackpot: false,
            message: `获得 ${prize.name}！`
        };
    }
    
    /**
     * 选择奖品
     * @returns {Object} 奖品对象
     */
    static selectPrize() {
        const prizes = this.LOTTERY_CONFIG.prizes;
        const random = Math.random() * 100;
        
        let cumulativeChance = 0;
        for (const prize of prizes) {
            cumulativeChance += prize.chance;
            if (random <= cumulativeChance) {
                return this.generatePrize(prize);
            }
        }
        
        // 默认返回第一个奖品
        return this.generatePrize(prizes[0]);
    }
    
    /**
     * 选择大奖
     * @returns {Object} 大奖对象
     */
    static selectJackpotPrize() {
        const jackpotPrizes = this.LOTTERY_CONFIG.jackpot.prizes;
        const randomIndex = Math.floor(Math.random() * jackpotPrizes.length);
        return this.generatePrize(jackpotPrizes[randomIndex]);
    }
    
    /**
     * 生成奖品对象
     * @param {Object} prizeConfig - 奖品配置
     * @returns {Object} 奖品对象
     */
    static generatePrize(prizeConfig) {
        const prize = { ...prizeConfig };
        
        // 处理随机装备
        if (prize.id === 'random_equipment_tier1' || 
            prize.id === 'random_equipment_tier2' || 
            prize.id === 'random_equipment_tier3') {
            // 从DropSystem获取随机装备
            const equipment = DropSystem.getRandomEquipment(prize.tier);
            if (equipment) {
                return equipment;
            }
        }
        
        // 处理随机碎片
        if (prize.id === 'random_fragment_tier1') {
            const fragments = DropSystem.generateRandomFragments(1, prize.tier);
            if (fragments.length > 0) {
                return fragments[0];
            }
        }
        
        return prize;
    }
    
    /**
     * 添加奖品到背包
     * @param {Object} player - 玩家对象
     * @param {Object} prize - 奖品对象
     */
    static addPrizeToInventory(player, prize) {
        if (!player.inventory) {
            player.inventory = [];
        }
        
        // 深拷贝奖品
        const prizeCopy = deepClone(prize);
        player.inventory.push(prizeCopy);
    }
    
    /**
     * 检查玩家是否有抽奖券
     * @param {Object} player - 玩家对象
     * @param {string} ticketType - 抽奖券类型
     * @returns {boolean} 是否有抽奖券
     */
    static hasTicket(player, ticketType = 'lottery_ticket_tier1') {
        if (!player || !player.inventory) {
            return false;
        }
        
        return player.inventory.some(
            item => item.id === ticketType || item.name === '抽奖券'
        );
    }
    
    /**
     * 获取玩家拥有的抽奖券数量
     * @param {Object} player - 玩家对象
     * @returns {number} 抽奖券数量
     */
    static getTicketCount(player) {
        if (!player || !player.inventory) {
            return 0;
        }
        
        return player.inventory.filter(
            item => item.id && item.id.includes('lottery_ticket') || 
                    item.name && item.name.includes('抽奖券')
        ).length;
    }
}

